import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Check, Plus, X } from "lucide-react";
import { imgUrl } from "@/config";
import { computeRentalPrice } from "@/utils/pricing";

// Asked right before a product with linked add-ons (e.g. a snack tray) goes into the cart.
export const AddonPrompt = ({ product, addons, range, onConfirm, onClose }) => {
  const [picked, setPicked] = useState([]);
  if (!product) return null;
  const toggle = (id) => setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  return createPortal(
    <div className="fixed inset-0 z-[130] bg-[#040A0F]/85 backdrop-blur-md flex items-center justify-center p-3" onClick={onClose} data-testid="addon-prompt">
      <div className="w-full max-w-md rounded-2xl border border-[#FFD166]/30 bg-[#0A1A26] shadow-[0_25px_80px_-10px_rgba(0,0,0,0.9)] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#FFD166]/15 flex items-start gap-3">
          <img src={imgUrl(product.image)} alt={product.name} className="w-14 h-14 rounded-lg object-contain bg-black/30 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="eyebrow">Before we add it…</p>
            <h3 className="font-serif text-xl text-[#F6F1E4] leading-tight mt-1" data-testid="addon-prompt-title">Want to add an extra to your {product.name}?</h3>
            <p className="text-xs text-[#B9C8D5] mt-1">These only fit this item, so we only offer them here.</p>
          </div>
          <button onClick={onClose} className="text-[#F6F1E4] hover:text-[#FFD166] p-1 -mr-1 -mt-1" aria-label="Close" data-testid="addon-prompt-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-2">
          {addons.map((a) => {
            const on = picked.includes(a.id);
            const pr = computeRentalPrice(a, range);
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => toggle(a.id)}
                className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 transition-colors ${on ? "border-[#FFD166] bg-[#FFD166]/10" : "border-[#FFD166]/20 hover:border-[#FFD166]/50"}`}
                data-testid={`addon-option-${a.id}`}
              >
                <img src={imgUrl(a.image)} alt={a.name} className="w-12 h-12 rounded-lg object-contain bg-black/30 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#F6F1E4] truncate">{a.name}</div>
                  <div className="text-[11px] text-[#B9C8D5]">
                    {pr ? <>+${pr.total.toFixed(0)} for your {pr.billableDays} day{pr.billableDays === 1 ? "" : "s"} <span className="text-[#B9C8D5]/70">(${pr.rate.toFixed(0)}/day)</span></> : `$${Number(a.tier_1_3 ?? a.price_per_day ?? 0).toFixed(0)}/day`}
                  </div>
                </div>
                <span className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${on ? "bg-[#FFD166] border-[#FFD166] text-[#040A0F]" : "border-[#FFD166]/40 text-[#FFD166]"}`}>
                  {on ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                </span>
              </button>
            );
          })}
        </div>
        <div className="p-5 pt-0 flex flex-col sm:flex-row gap-2">
          <button type="button" onClick={() => onConfirm([])} className="btn-ghost text-sm flex-1" data-testid="addon-prompt-skip">No thanks, just the {product.category === "strollers" ? "stroller" : "item"}</button>
          <button type="button" onClick={() => onConfirm(picked)} className="btn-primary text-sm flex-1" data-testid="addon-prompt-confirm">
            {picked.length > 0 ? `Add with ${picked.length} extra${picked.length === 1 ? "" : "s"}` : "Add to cart"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
